// Stratul „Крупный игрок": urmele banilor mari, desenate peste lumânări.
//
// Patru lucruri, fiecare cu culoarea lui — aceeași în desen și în legendă, fiindcă
// ambele citesc din `LEGEND`. O legendă scrisă separat de desen ajunge, după prima
// schimbare de culoare, să mintă.
//
// * **Plotnostile** — ordinele limită mari din stakanele Binance, Bybit, OKX și Bitget,
//   citite prin REST la câteva secunde (stream-urile dau prea puține niveluri). Fiecare
//   e urmărită în timp: cât stă, dacă e mâncată (prețul a ajuns la ea și ea a dispărut)
//   sau retrasă (a dispărut înainte să ajungă prețul — capcana clasică).
// * **Ordinele mari de piață** — bule pe lumânări, de la server (fluxul agregat).
// * **Absorbția** — benzile unde agresorul a lovit mult și prețul n-a cedat, de la server.
// * **Profilul de volum** — din lumânările graficului, cu POC (prețul cu cel mai mult volum).

import { el, fmtPrice } from '../core/util.js';
import { restDepth } from '../core/exchanges.js';

const BURSE = ['binance', 'bybit', 'okx', 'bitget'];
const SCURT = { binance: 'BN', bybit: 'BY', okx: 'OKX', bitget: 'BG' };

/** Culorile și înțelesul fiecărui element. Sursa unică pentru desen și legendă. */
export const LEGEND = [
  { id: 'walls', group: 'walls', color: '#2dd4bf', shape: 'bar', title: 'Плотность на покупку',
    text: 'Крупная лимитная заявка на покупку (bid) в стакане одной из 4 бирж. Длина полосы у правого края — размер. Подпись: сумма · биржа · сколько стоит.' },
  { id: 'walls_ask', group: 'walls', color: '#f472b6', shape: 'bar', title: 'Плотность на продажу',
    text: 'Крупная заявка на продажу (ask). Цена, подходя к ней, либо разъест её (пробой), либо отскочит.' },
  { id: 'eating', group: 'walls', color: '#fbbf24', shape: 'bar', title: 'Плотность разъедается',
    text: 'Цена у плотности, и она уменьшается — её съедают рыночными ордерами. Часто предвестник пробоя.' },
  { id: 'eaten', group: 'walls', color: '#fbbf24', shape: 'dash', title: '✕ Съедена',
    text: 'Плотность исчезла, когда цена до неё дошла: её реально выкупили/продали. Уровень пробит деньгами.' },
  { id: 'pulled', group: 'walls', color: '#8b93a7', shape: 'dash', title: '⌀ Снята',
    text: 'Плотность исчезла до подхода цены — её убрали. Это была приманка (спуфинг): на такой уровень не опирайтесь.' },
  { id: 'buy', group: 'prints', color: '#22c55e', shape: 'dot', title: 'Крупная покупка по рынку',
    text: 'Один рыночный ордер на покупку из самых больших 0.5% по этой монете, на любой из 4 бирж. Размер круга — сумма.' },
  { id: 'sell', group: 'prints', color: '#ef4444', shape: 'dot', title: 'Крупная продажа по рынку',
    text: 'То же, на продажу. Скопление кругов в одном месте — там работал крупный игрок.' },
  { id: 'absorb_sell', group: 'absorb', color: '#a78bfa', shape: 'band', title: 'Поглощение продаж',
    text: 'Здесь агрессивно продавали намного больше обычного, но цена не пошла ниже: всё выкупил крупный лимитный покупатель. Поддержка.' },
  { id: 'absorb_buy', group: 'absorb', color: '#c084fc', shape: 'band', title: 'Поглощение покупок',
    text: 'Агрессивно покупали, а цена не выросла: всё продал крупный лимитный продавец. Сопротивление.' },
  { id: 'profile', group: 'profile', color: '#64748b', shape: 'bar', title: 'Профиль объёма',
    text: 'Сколько объёма прошло на каждой цене за видимую историю (по свечам Binance). Где полоса длиннее — там крупные набирали позицию.' },
  { id: 'poc', group: 'profile', color: '#facc15', shape: 'dash', title: 'POC',
    text: 'Цена с самым большим объёмом. Цена к ней часто возвращается и вокруг неё «застревает».' },
];
const C = Object.fromEntries(LEGEND.map((l) => [l.id, l.color]));

/** Liniile planului și ale formației — nu sunt ale stratului, dar intră în legendă. */
export const PLAN_COLORS = {
  level: '#969aa8', entry: '#0f8bfd', stop: '#f6455f', target: '#16c784', target1: '#4ade80',
  bounceEntry: '#a78bfa', bounceStop: '#fb923c',
};
export const PLAN_LEGEND = [
  { for: 'all', color: PLAN_COLORS.level, shape: 'dash', short: 'уровень', title: 'Уровень', text: 'Уровень, от которого сканер нашёл формацию, или к которому подходит цена.' },
  { for: 'setup', color: PLAN_COLORS.entry, shape: 'dotted', short: 'вход', title: 'Вход', text: 'Цена входа стоп-ордером (только у сжатия — вход на пробое).' },
  { for: 'setup', color: PLAN_COLORS.stop, shape: 'line', short: 'стоп', title: 'Стоп', text: 'Где сетап перестаёт быть верным. Выход с убытком 1R.' },
  { for: 'all', color: PLAN_COLORS.target1, shape: 'dash', short: 'цель 1', title: 'Цель 1 · половина', text: 'Ближняя из двух: +1.5% или 2R. Здесь закрывается половина позиции, а стоп переносится в точку входа — остаток уже не может проиграть.' },
  { for: 'all', color: PLAN_COLORS.target, shape: 'line', short: 'цель 2', title: 'Цель 2 · остаток', text: 'Дальняя из двух: 2R или +1.5%. Здесь закрывается остаток.' },
  { for: 'approach', color: PLAN_COLORS.entry, shape: 'dotted', short: 'вход пробоя', title: 'Вход пробоя', text: 'Стоп-ордер чуть за уровнем: срабатывает, только если цена уровень проходит.' },
  { for: 'approach', color: PLAN_COLORS.stop, shape: 'dash', short: 'стоп пробоя', title: 'Стоп пробоя', text: 'Цена вернулась обратно в диапазон — пробой не состоялся.' },
  { for: 'approach', color: PLAN_COLORS.bounceEntry, shape: 'dotted', short: 'вход отскока', title: 'Вход отскока', text: 'Лимитный ордер чуть перед уровнем: исполняется, когда цена в уровень упирается.' },
  { for: 'approach', color: PLAN_COLORS.bounceStop, shape: 'dash', short: 'стоп отскока', title: 'Стоп отскока', text: 'Цена прошла уровень — отскока не будет.' },
  { for: 'all', color: '#b2b6c6', shape: 'thin', short: null, title: 'Авто-уровни ×N', text: 'Уровни поддержки/сопротивления по экстремумам; ×N — сколько раз цена их касалась. Только на большом графике.' },
];

/** Rândurile de legendă care se aplică unui card: formație sau apropiere. */
export function planLegendFor(kind) {
  const fel = kind === 'approach' ? 'approach' : 'setup';
  return PLAN_LEGEND.filter((l) => l.for === 'all' || l.for === fel);
}

// ------------------------------------------------------------------ date

/**
 * Datele stratului pentru o monedă: stakanele de pe 4 burse (din browser) și urmele de
 * la server. Un singur obiect pentru grafic și pentru panoul de lângă el.
 */
export class WhaleFeed {
  constructor(symbol, { quoteVolume = 0, onUpdate } = {}) {
    this.symbol = symbol;
    this.quoteVolume = quoteVolume;
    this.onUpdate = onUpdate;
    this.walls = [];         // ce desenăm: în picioare + fantome (mâncate / retrase)
    this.track = new Map();  // cheie -> zid urmărit
    this.prints = [];
    this.absorption = [];
    this.flow = null;
    this.threshold = null;
    // Extremele prețului de la ultima citire a fiecărei burse: un zid dispărut e
    // „mâncat" doar dacă prețul a ajuns la el între cele două citiri ale bursei lui.
    this.ext = Object.fromEntries(BURSE.map((ex) => [ex, { hi: null, lo: null }]));
    this.lastPrice = null;
    this.timers = [];
    this.stopped = false;
  }

  start() {
    // Bursele se citesc decalat, ca să nu plece patru cereri în aceeași milisecundă.
    this.dead = new Set();
    BURSE.forEach((ex, i) => {
      const run = () => { if (!this.dead.has(ex)) this.#pollBook(ex); };
      this.timers.push(setTimeout(() => { run(); this.timers.push(setInterval(run, 4000)); }, i * 600));
    });
    this.#pollServer();
    this.timers.push(setInterval(() => this.#pollServer(), 5000));
  }

  stop() {
    this.stopped = true;
    for (const t of this.timers) { clearTimeout(t); clearInterval(t); }
    this.timers = [];
  }

  /** Prețul viu, din graficul care e oricum abonat la lumânări. */
  notePrice(price) {
    if (!price) return;
    this.lastPrice = price;
    for (const e of Object.values(this.ext)) {
      e.hi = Math.max(e.hi ?? price, price);
      e.lo = Math.min(e.lo ?? price, price);
    }
  }

  async #pollServer() {
    try {
      const d = await (await fetch(`/api/whales?symbol=${encodeURIComponent(this.symbol)}`)).json();
      if (this.stopped) return;
      this.flow = d.flow;
      this.prints = d.whales?.prints || [];
      this.absorption = d.whales?.absorption || [];
      this.threshold = d.whales?.threshold || null;
      this.watching = d.watching;
      this.onUpdate?.();
    } catch { /* serverul își revine; următoarea citire încearcă din nou */ }
  }

  async #pollBook(ex) {
    let book;
    try {
      book = await restDepth(ex, this.symbol);
    } catch (err) {
      // 400/404: bursa nu are moneda — n-o mai întrebăm din 4 în 4 secunde. Altă eroare
      // (rețea, 429) e trecătoare, deci încercăm din nou la următoarea citire.
      if (/\b(400|404)\b/.test(String(err?.message || err))) this.dead.add(ex);
      return;
    }
    if (this.stopped || !book?.bids?.length || !book?.asks?.length) return;
    const mid = (book.bids[0][0] + book.asks[0][0]) / 2;
    this.notePrice(mid);
    const now = Date.now();

    // Pragul unei plotnosti: de șase ori nivelul tipic al stakanului acestei burse și
    // cel puțin $15k sau 0.02% din oborotul zilei. Pragul fix ar fi greșit în ambele
    // direcții: $50k e un zid pe o monedă mică și nimic pe DOGE.
    const inRange = (rows) => rows.filter(([p]) => Math.abs(p - mid) / mid <= 0.04);
    const bids = inRange(book.bids);
    const asks = inRange(book.asks);
    const marimi = [...bids, ...asks].map((r) => r[1]).sort((a, b) => a - b);
    const mediana = marimi[Math.floor(marimi.length / 2)] || 0;
    const prag = Math.max(mediana * 6, 15000, this.quoteVolume * 0.0002);

    const vazute = new Set();
    for (const [side, rows] of [['bid', bids], ['ask', asks]]) {
      const mari = rows.filter((r) => r[1] >= prag).sort((a, b) => b[1] - a[1]).slice(0, 6);
      for (const [price, usd] of mari) {
        const key = `${ex}|${side}|${price}`;
        vazute.add(key);
        const w = this.track.get(key);
        if (!w) {
          this.track.set(key, {
            key, ex, side, price, usd, peak: usd, first: now, last: now, state: 'stands',
            // Uriaș din prima citire: îl arătăm imediat, fără perioada de probă.
            big: usd >= prag * 4,
          });
        } else {
          if (w.gone) {
            // Retrasă și pusă la loc: e un ordin nou, cu vârsta lui de la zero. Fără asta,
            // fantoma și zidul viu ar fi apărut amândouă, cu același preț.
            delete w.gone;
            w.first = now;
            w.peak = usd;
          }
          w.usd = usd;
          w.peak = Math.max(w.peak, usd);
          w.last = now;
          // Prețul e lângă ea și ea scade: o mănâncă cineva chiar acum.
          const aproape = Math.abs(price - mid) / mid <= 0.003;
          w.state = aproape && usd < w.peak * 0.7 ? 'eating' : 'stands';
        }
      }
    }

    // Ce a dispărut de pe bursa asta: mâncată dacă prețul a ajuns la ea, retrasă altfel.
    const hi = Math.max(this.ext[ex].hi ?? mid, mid);
    const lo = Math.min(this.ext[ex].lo ?? mid, mid);
    for (const w of this.track.values()) {
      if (w.ex !== ex || vazute.has(w.key)) continue;
      if (w.state === 'eaten' || w.state === 'pulled') continue;
      const atins = w.side === 'bid' ? lo <= w.price * 1.001 : hi >= w.price * 0.999;
      w.state = atins ? 'eaten' : 'pulled';
      w.gone = now;
    }
    // Fantomele stau un minut și jumătate — destul cât să vezi ce s-a întâmplat.
    for (const [k, w] of this.track) {
      if (w.gone && now - w.gone > 90000) this.track.delete(k);
    }
    this.ext[ex] = { hi: mid, lo: mid };
    this.#publish();
  }

  #publish() {
    const now = Date.now();
    const toate = [...this.track.values()]
      // Un zid apărut acum câteva secunde poate fi o momeală; arătăm doar ce a stat
      // cel puțin 15 secunde, sau ce e uriaș din prima.
      .filter((w) => w.gone || now - w.first >= 15000 || w.big)
      .sort((a, b) => b.usd - a.usd);
    const vii = toate.filter((w) => !w.gone).slice(0, 14);
    const fantome = toate.filter((w) => w.gone).slice(0, 8);
    this.walls = [...vii, ...fantome].map((w) => ({
      ...w, age: Math.round(((w.gone || now) - w.first) / 1000),
    }));
    this.onUpdate?.();
  }

  /** Pentru AI: ce e pe ecran, în cifre. */
  summary() {
    return this.walls.slice(0, 16).map((w) => ({
      price: w.price, usd: Math.round(w.usd), side: w.side, exchange: w.ex,
      seconds: w.age, state: w.state,
    }));
  }
}

// ----------------------------------------------------------------- desen

function money(usd) {
  if (usd >= 1e6) return `$${(usd / 1e6).toFixed(usd >= 1e7 ? 0 : 1)}M`;
  if (usd >= 1e3) return `$${Math.round(usd / 1e3)}k`;
  return `$${Math.round(usd)}`;
}

function varsta(s) {
  if (s < 60) return `${s}с`;
  if (s < 3600) return `${Math.floor(s / 60)}м`;
  return `${Math.floor(s / 3600)}ч`;
}

/** Profilul de volum din lumânări: volumul fiecărei bare, împărțit uniform pe amplitudinea ei. */
export function volumeProfile(bars, cells = 70) {
  if (!bars?.length) return null;
  let lo = Infinity;
  let hi = -Infinity;
  for (const b of bars) { lo = Math.min(lo, b.low); hi = Math.max(hi, b.high); }
  if (!(hi > lo)) return null;
  const step = (hi - lo) / cells;
  const vol = new Array(cells).fill(0);
  for (const b of bars) {
    const a = Math.max(0, Math.floor((b.low - lo) / step));
    const z = Math.min(cells - 1, Math.floor((b.high - lo) / step));
    const parte = (b.volume || 0) / (z - a + 1);
    for (let i = a; i <= z; i++) vol[i] += parte;
  }
  let poc = 0;
  for (let i = 1; i < cells; i++) if (vol[i] > vol[poc]) poc = i;
  return { lo, step, vol, poc, pocPrice: lo + (poc + 0.5) * step, max: vol[poc] };
}

/** Nodurile profilului (maxime locale), pentru AI. */
export function profileNodes(prof, n = 6) {
  if (!prof) return [];
  const noduri = [];
  for (let i = 1; i < prof.vol.length - 1; i++) {
    if (prof.vol[i] >= prof.vol[i - 1] && prof.vol[i] >= prof.vol[i + 1]) {
      noduri.push({ price: prof.lo + (i + 0.5) * prof.step, share: prof.vol[i] / prof.max, poc: i === prof.poc });
    }
  }
  return noduri.sort((a, b) => b.share - a.share).slice(0, n)
    .map((x) => ({ ...x, share: Math.round(x.share * 100) / 100 }));
}

export class WhaleLayer {
  constructor(chart, feed) {
    this.chart = chart;
    this.feed = feed;
    this.show = { walls: true, prints: true, absorb: true, profile: true };
    this.host = chart.mainEl;
    this.canvas = el('canvas', { class: 'chart-whales-layer' });
    this.ctx = this.canvas.getContext('2d');
    this.host.append(this.canvas);
    this.ro = new ResizeObserver(() => this.#resize());
    this.ro.observe(this.host);
    this.#resize();
  }

  setVisible(group, on) {
    this.show[group] = on;
    this.render();
  }

  #resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.render();
  }

  render() {
    const ctx = this.ctx;
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
    const series = this.chart.candles;
    if (!series || !this.chart.bars?.length) return;
    let axa = 60;
    try { axa = this.chart.chart.priceScale('right').width() || 60; } catch { /* implicit */ }
    const w = this.host.clientWidth - axa;   // zona lumânărilor, fără axa de preț
    const h = this.host.clientHeight;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();
    ctx.font = '10px Manrope, sans-serif';
    ctx.textBaseline = 'middle';

    if (this.show.profile) this.#profile(ctx, series, w);
    if (this.show.absorb) this.#absorb(ctx, series, w);
    if (this.show.walls) this.#walls(ctx, series, w);
    if (this.show.prints) this.#prints(ctx, series);
    ctx.restore();
  }

  #profile(ctx, series, w) {
    const prof = volumeProfile(this.chart.bars);
    if (!prof) return;
    const lat = Math.min(140, w * 0.16);
    for (let i = 0; i < prof.vol.length; i++) {
      const y1 = series.priceToCoordinate(prof.lo + i * prof.step);
      const y2 = series.priceToCoordinate(prof.lo + (i + 1) * prof.step);
      if (y1 === null || y2 === null) continue;
      const len = (prof.vol[i] / prof.max) * lat;
      ctx.fillStyle = i === prof.poc ? 'rgba(250, 204, 21, 0.45)' : 'rgba(100, 116, 139, 0.28)';
      ctx.fillRect(w - len, Math.min(y1, y2) + 0.5, len, Math.max(1, Math.abs(y2 - y1) - 1));
    }
    const y = series.priceToCoordinate(prof.pocPrice);
    if (y !== null) {
      ctx.strokeStyle = C.poc;
      ctx.setLineDash([2, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = C.poc;
      ctx.fillText('POC', 6, y - 7);
    }
  }

  #absorb(ctx, series, w) {
    for (const a of this.feed.absorption || []) {
      const y = series.priceToCoordinate(a.price);
      if (y === null) continue;
      const p = this.chart.toPixel({ time: a.t, price: a.price });
      const x0 = Math.max(0, p ? p.x : 0);
      const culoare = C[a.kind] || C.absorb_sell;
      ctx.fillStyle = `${culoare}2e`;
      ctx.fillRect(x0, y - 5, w - x0, 10);
      ctx.strokeStyle = culoare;
      ctx.lineWidth = 1;
      ctx.strokeRect(x0 + 0.5, y - 5, w - x0 - 1, 10);
      ctx.fillStyle = culoare;
      const t = a.kind === 'absorb_sell' ? 'Поглощение продаж' : 'Поглощение покупок';
      ctx.fillText(`${t} ${money(a.usd)}`, x0 + 6, y - 12);
    }
  }

  #walls(ctx, series, w) {
    const walls = this.feed.walls || [];
    if (!walls.length) return;
    const max = Math.max(...walls.map((x) => x.peak || x.usd));
    const lat = Math.min(220, w * 0.28);
    const randuri = [];
    for (const wall of walls) {
      const y = series.priceToCoordinate(wall.price);
      if (y === null) continue;
      const culoare = wall.state === 'pulled' ? C.pulled
        : (wall.state === 'eaten' || wall.state === 'eating') ? C.eating
          : wall.side === 'bid' ? C.walls : C.walls_ask;
      const fantoma = wall.state === 'eaten' || wall.state === 'pulled';
      // Linia subțire pe toată lățimea: unde e nivelul. Bara groasă de la dreapta: cât e.
      ctx.strokeStyle = culoare;
      ctx.globalAlpha = fantoma ? 0.55 : 0.5;
      ctx.lineWidth = 1;
      ctx.setLineDash(fantoma ? [5, 4] : []);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = fantoma ? 0.35 : 0.85;
      const len = Math.max(8, Math.sqrt((wall.peak || wall.usd) / max) * lat);
      ctx.fillStyle = culoare;
      ctx.fillRect(w - len, y - 3, len, 6);
      ctx.globalAlpha = 1;

      if (randuri.some((r) => Math.abs(r - y) < 12)) continue;
      randuri.push(y);
      const stare = wall.state === 'eating' ? ' · съедается'
        : wall.state === 'eaten' ? ' · ✕ съедена' : wall.state === 'pulled' ? ' · ⌀ снята' : '';
      const text = `${money(wall.usd)} · ${SCURT[wall.ex]} · ${varsta(wall.age)}${stare}`;
      const tw = ctx.measureText(text).width;
      const tx = w - len - tw - 8;
      ctx.fillStyle = 'rgba(11, 14, 22, 0.75)';
      ctx.fillRect(tx - 3, y - 7, tw + 6, 14);
      ctx.fillStyle = culoare;
      ctx.fillText(text, tx, y);
    }
  }

  #prints(ctx, series) {
    const prag = this.feed.threshold || 1;
    for (const p of this.feed.prints || []) {
      const pt = this.chart.toPixel({ time: p.t, price: p.p });
      if (!pt) continue;
      const r = Math.min(16, 3.5 + 4 * Math.sqrt(p.usd / prag));
      const culoare = p.side === 'buy' ? C.buy : C.sell;
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
      ctx.fillStyle = `${culoare}55`;
      ctx.fill();
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = culoare;
      ctx.stroke();
      if (p.usd >= prag * 3) {
        ctx.fillStyle = culoare;
        ctx.fillText(money(p.usd), pt.x + r + 3, pt.y);
      }
    }
  }

  destroy() {
    this.ro?.disconnect();
    this.canvas.remove();
  }
}

export { money, varsta, fmtPrice };
